import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { transferServices } from '../transferServices'
import {
  CreatePurchaseOnRampPayload,
  CreatePurchaseOnRampDTO,
  CreatePurchaseOnRampDTOLink,
} from '../transferTypes'
import { mapAxiosErrorToApiError } from '@/utils/mapAxiosErrorToApiError'

interface IOptionsUseMutation<T> {
  onSuccess?: (data: T) => void
  onError?: (error: { errorMessage: string; code?: string }) => void
}

export const useCreatePurchaseOnRamp = (
  options?: IOptionsUseMutation<CreatePurchaseOnRampDTO>,
) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation<
    CreatePurchaseOnRampDTO,
    Error,
    CreatePurchaseOnRampPayload
  >({
    mutationFn: (data) => transferServices.createPurchaseOnRamp(data),
    onSuccess: (data) => {
      if (options?.onSuccess) {
        options.onSuccess(data)
      }

      // Invalidate transactions queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['TRANSACTIONS'],
      })
    },
    onError: (err: any) => {
      // Map axios error to API error format
      const apiError = mapAxiosErrorToApiError(err)
      let errorMessage = t('api:errors.somethingWrong')
      let code: string | undefined = apiError.code

      // Handle specific error cases
      if (
        apiError.code === 'EMAIL_NOT_CONFIRMED' ||
        err.message?.includes('EMAIL_NOT_CONFIRMED')
      ) {
        errorMessage = t('api:emailNotConfirmed')
        code = 'EMAIL_NOT_CONFIRMED'
      }

      // Handle USER_NOT_VERIFIED error
      if (
        apiError.code === 'USER_NOT_VERIFIED' ||
        err.message?.includes('USER_NOT_VERIFIED') ||
        err.response?.data?.detail?.code === 'USER_NOT_VERIFIED'
      ) {
        errorMessage = t('api:errors.userNotVerified')
        code = 'USER_NOT_VERIFIED'
      }

      if (options?.onError) {
        options.onError({ errorMessage, code })
      }
    },
  })

  async function createPurchaseOnRamp(data: CreatePurchaseOnRampPayload) {
    return mutation.mutateAsync(data)
  }

  return {
    createPurchaseOnRamp,
    ...mutation,
  }
}

export const useCreatePurchaseOnRampLink = (
  options?: IOptionsUseMutation<CreatePurchaseOnRampDTOLink>,
) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation<
    CreatePurchaseOnRampDTOLink,
    Error,
    CreatePurchaseOnRampPayload
  >({
    mutationFn: (data) => transferServices.createPurchaseOnRampLink(data),
    onSuccess: (data) => {
      if (options?.onSuccess) {
        options.onSuccess(data)
      }

      // Invalidate transactions queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['TRANSACTIONS'],
      })
    },
    onError: (err: any) => {
      // Map axios error to API error format
      const apiError = mapAxiosErrorToApiError(err)
      let errorMessage = t('api:errors.somethingWrong')
      let code: string | undefined = apiError.code

      // Handle specific error cases
      if (
        apiError.code === 'EMAIL_NOT_CONFIRMED' ||
        err.message?.includes('EMAIL_NOT_CONFIRMED')
      ) {
        errorMessage = t('api:emailNotConfirmed')
        code = 'EMAIL_NOT_CONFIRMED'
      }

      // Handle USER_NOT_VERIFIED error
      if (
        apiError.code === 'USER_NOT_VERIFIED' ||
        err.message?.includes('USER_NOT_VERIFIED') ||
        err.response?.data?.detail?.code === 'USER_NOT_VERIFIED'
      ) {
        errorMessage = t('api:errors.userNotVerified')
        code = 'USER_NOT_VERIFIED'
      }

      if (options?.onError) {
        options.onError({ errorMessage, code })
      }
    },
  })

  async function createPurchaseOnRampLink(data: CreatePurchaseOnRampPayload) {
    return mutation.mutateAsync(data)
  }

  return {
    createPurchaseOnRampLink,
    ...mutation,
  }
}
